import { createFileRoute } from "@tanstack/react-router";
import {
  useGetProfile,
  useUpdateProfile,
  useUploadAvatar,
  useDeleteAccount,
  getGetProfileQueryKey,
} from "@/services/api/users/users";
import { useForm, Controller } from "react-hook-form";
import { UserSector } from "@/types/api/userSector";
import { ScottishLocalAuthority } from "@/types/api/scottishLocalAuthority";
import { USER_SECTOR_LABELS, SCOTTISH_LOCAL_AUTHORITY_LABELS } from "@/utils/enum-labels";
import { useEffect, useState } from "react";
import { Camera01, Save01, AlertCircle, RefreshCw01, Trash01 } from "@untitledui/icons";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { Button } from "@/components/base/buttons/button";
import { FileTrigger } from "@/components/base/file-upload-trigger/file-upload-trigger";
import { Avatar } from "@/components/base/avatar/avatar";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { useQueryClient } from "@tanstack/react-query";
import type { UpdateProfileCommand } from "@/types/api";
import { Toggle } from "@/components/base/toggle/toggle";
import { useAuthStore } from "@/stores/authStore";
import { useCookieConsentStore } from "@/stores/cookieConsentStore";
import { DestructiveHorizontalModal } from "@/components/application/modals/destructive-horizontal-modal";
import { toastSuccess, toastError } from "@/utils/toast";
import { useIltEnabled } from "@/hooks/useFeatureFlags";

export const Route = createFileRoute("/_app/profile")({
  component: ProfilePage,
});

const MAX_AVATAR_BYTES = 5 * 1024 * 1024; // 5MB
const MAX_EMPLOYER_LENGTH = 200;

/** Edits account details and email preferences, exposing the learner reminder setting while ILT is enabled. */
function ProfilePage() {
  const queryClient = useQueryClient();
  const { user, setUser, clearAuth } = useAuthStore();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Cookie consent state
  const analyticsEnabled = useCookieConsentStore((state) => state.preferences.analytics);
  const updateCookiePreferences = useCookieConsentStore((state) => state.updatePreferences);

  const { data: profile, isLoading, isError, refetch } = useGetProfile();
  const iltEnabled = useIltEnabled();

  const updateProfileMutation = useUpdateProfile();
  const uploadAvatarMutation = useUploadAvatar();
  const deleteAccountMutation = useDeleteAccount();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, dirtyFields },
  } = useForm<UpdateProfileCommand>({
    mode: "onBlur",
    defaultValues: {
      displayName: "",
      sector: undefined,
      localAuthority: undefined,
      employer: "",
      receiveNewsletter: false,
      receiveIltReminders: true,
    },
  });

  useEffect(() => {
    if (profile) {
      reset({
        displayName: profile.displayName ?? "",
        sector: profile.sector,
        localAuthority: profile.localAuthority,
        employer: profile.employer ?? "",
        receiveNewsletter: profile.receiveNewsletter ?? false,
        receiveIltReminders: profile.receiveIltReminders ?? true,
      });
    }
  }, [profile, reset]);

  const onSubmit = (data: UpdateProfileCommand) => {
    updateProfileMutation.mutate(
      {
        data: {
          ...data,
          // Blank clears the employer (LMS-211); the API trims it too.
          employer: data.employer?.trim() ?? "",
          // An unrelated edit must not undo an unsubscribe made since this profile was loaded.
          receiveIltReminders: dirtyFields.receiveIltReminders ? data.receiveIltReminders : null,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetProfileQueryKey() });
          // Update authStore.user with new profile data so derived state (e.g., isProfileIncomplete) updates immediately
          if (user) {
            setUser({
              ...user,
              displayName: data.displayName ?? undefined,
              sector: data.sector ?? undefined,
              localAuthority: data.localAuthority ?? undefined,
            });
          }
          toastSuccess("Profile updated", "Your profile details have been successfully updated.");
        },
        onError: (error) => {
          toastError(
            "Update failed",
            error instanceof Error ? error.message : "Failed to update profile. Please try again.",
          );
        },
      },
    );
  };

  const handleAvatarUpload = (files: FileList | null) => {
    if (files && files.length > 0) {
      const file = files[0];

      if (file.size > MAX_AVATAR_BYTES) {
        toastError(
          "Upload failed",
          `File is too large. Maximum size is ${MAX_AVATAR_BYTES / 1024 / 1024} MB.`,
        );
        return;
      }

      uploadAvatarMutation.mutate(
        { data: { file } },
        {
          onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: getGetProfileQueryKey() });
            // Update authStore.user.profileImageUrl so it reflects immediately in the UI
            if (user && response?.profileImageUrl) {
              setUser({
                ...user,
                profileImageUrl: response.profileImageUrl,
              });
            }
            toastSuccess("Avatar uploaded", "Your profile picture has been updated.");
          },
          onError: (error) => {
            toastError(
              "Upload failed",
              error instanceof Error
                ? error.message
                : "Failed to upload avatar. Please check the file size and type.",
            );
          },
        },
      );
    }
  };

  const handleDeleteAccount = () => {
    deleteAccountMutation.mutate(undefined, {
      onSuccess: (data) => {
        // Clear authStore state before redirecting
        clearAuth();
        // Redirect to WorkOS logout URL to clear session, or fallback to home
        window.location.href = data?.logoutUrl ?? "/";
      },
      onError: (error) => {
        toastError(
          "Deletion failed",
          error instanceof Error ? error.message : "Failed to delete account. Please try again.",
        );
        setIsDeleteModalOpen(false);
      },
    });
  };

  if (isLoading)
    return (
      <div className="flex h-full items-center justify-center p-12">
        <div className="size-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600"></div>
      </div>
    );

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center p-12">
        <EmptyState>
          <EmptyState.Header>
            <EmptyState.FeaturedIcon icon={AlertCircle} color="error" />
          </EmptyState.Header>
          <EmptyState.Content>
            <EmptyState.Title>Failed to load profile</EmptyState.Title>
            <EmptyState.Description>
              We couldn't retrieve your profile information. Please try again.
            </EmptyState.Description>
          </EmptyState.Content>
          <EmptyState.Footer>
            <Button onClick={() => refetch()} iconLeading={RefreshCw01}>
              Retry
            </Button>
          </EmptyState.Footer>
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-8 py-12">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-gray-900">My Profile</h1>
        <p className="mt-2 text-gray-600">Manage your personal details and preferences.</p>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* Left Column: Avatar & Info */}
        <div className="col-span-1 space-y-6">
          <div className="flex flex-col items-center rounded-xl border border-gray-200 bg-white p-6 text-center">
            <div className="group relative mb-4">
              <Avatar
                size="2xl"
                src={profile?.profileImageUrl ?? undefined}
                alt={profile?.displayName || profile?.firstName || "User"}
                className="size-32 text-4xl"
              />
              <div className="absolute right-0 bottom-0">
                <FileTrigger
                  acceptedFileTypes={["image/png", "image/jpeg", "image/webp"]}
                  onSelect={handleAvatarUpload}
                >
                  <Button
                    size="sm"
                    color="secondary"
                    className="h-auto rounded-full p-2"
                    isLoading={uploadAvatarMutation.isPending}
                  >
                    <Camera01 className="size-4" />
                  </Button>
                </FileTrigger>
              </div>
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              {profile?.displayName || profile?.firstName}
            </h3>
            <p className="text-sm text-gray-500">{profile?.email}</p>
          </div>
        </div>

        {/* Right Column: Form Sections */}
        <div className="col-span-1 space-y-6 lg:col-span-2">
          {/* Personal Details */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-lg font-medium text-gray-900">Personal Details</h2>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 gap-6">
                <Controller
                  name="displayName"
                  control={control}
                  rules={{
                    maxLength: {
                      value: 100,
                      message: "Display name must be 100 characters or less",
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      label="Choose your display name"
                      placeholder="Enter your name..."
                      error={error?.message}
                    />
                  )}
                />

                <Controller
                  name="sector"
                  control={control}
                  render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                    <Select
                      {...field}
                      label="Add your sector"
                      placeholder="Choose your sector from the list..."
                      value={value}
                      onChange={onChange}
                      items={Object.values(UserSector).map((s) => ({
                        id: s,
                        label: USER_SECTOR_LABELS[s],
                      }))}
                      isInvalid={!!error}
                      hint={error?.message}
                    >
                      {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                    </Select>
                  )}
                />

                <Controller
                  name="localAuthority"
                  control={control}
                  render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                    <Select
                      {...field}
                      label="Add your area"
                      placeholder="Choose your area from the list..."
                      value={value}
                      onChange={onChange}
                      items={Object.values(ScottishLocalAuthority).map((s) => ({
                        id: s,
                        label: SCOTTISH_LOCAL_AUTHORITY_LABELS[s],
                      }))}
                      isInvalid={!!error}
                      hint={error?.message}
                    >
                      {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                    </Select>
                  )}
                />

                {/* LMS-211: recorded on the learner's course feedback */}
                <Controller
                  name="employer"
                  control={control}
                  rules={{
                    validate: (value) =>
                      (value ?? "").trim().length <= MAX_EMPLOYER_LENGTH ||
                      `Employer must be ${MAX_EMPLOYER_LENGTH} characters or fewer`,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      label="Add your employer"
                      placeholder="Enter the organisation you work for..."
                      hint="The organisation you work for. We use it on your course feedback."
                      error={error?.message}
                    />
                  )}
                />
              </div>

              {/* Account Details (Read Only) */}
              <div className="border-t border-gray-200 pt-6">
                <h2 className="mb-4 text-lg font-medium text-gray-900">Account Details</h2>
                <div className="grid grid-cols-1 gap-6">
                  <Input
                    value={profile?.email ?? ""}
                    label="Email Address"
                    isDisabled
                    hint="Your email is managed by your organisation's identity provider and cannot be changed here."
                    isReadOnly
                  />
                </div>
              </div>

              {/* Privacy Settings */}
              <div className="border-t border-gray-200 pt-6">
                <h2 className="mb-4 text-lg font-medium text-gray-900">Privacy Settings</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Newsletter</p>
                      <p className="text-sm text-gray-500">
                        Receive infrequent updates and news about the platform.
                      </p>
                    </div>
                    <Controller
                      name="receiveNewsletter"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Toggle isSelected={!!value} onChange={onChange} aria-label="Newsletter" />
                      )}
                    />
                  </div>

                  {/* LMS-147: reminders before booked face-to-face sessions; booking emails go out either way */}
                  {iltEnabled && (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Face-to-face session reminders
                        </p>
                        <p className="text-sm text-gray-500">
                          Get an email 7 days and 1 day before each session you&apos;ve booked.
                          You&apos;ll still get booking confirmation and session cancellation
                          emails.
                        </p>
                      </div>
                      <Controller
                        name="receiveIltReminders"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <Toggle
                            isSelected={!!value}
                            onChange={onChange}
                            aria-label="Face-to-face session reminders"
                          />
                        )}
                      />
                    </div>
                  )}

                  {/* Cookie Preferences */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Analytics cookies</p>
                      <p className="text-sm text-gray-500">
                        Allow us to collect anonymous usage data to improve our services. Changes
                        take effect immediately.
                      </p>
                    </div>
                    <Toggle
                      isSelected={analyticsEnabled}
                      onChange={(enabled) => updateCookiePreferences({ analytics: enabled })}
                      aria-label="Analytics cookies"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end border-t border-gray-200 pt-6">
                <Button
                  type="submit"
                  iconLeading={Save01}
                  isDisabled={!isDirty || updateProfileMutation.isPending}
                  isLoading={updateProfileMutation.isPending}
                >
                  Save Changes
                </Button>
              </div>
            </form>
          </div>

          {/* Danger Zone */}
          <div className="rounded-xl border border-red-200 bg-red-50 p-6">
            <h2 className="mb-2 text-lg font-medium text-red-900">Delete Account</h2>
            <p className="mb-4 text-sm text-red-700">
              Permanently remove your account and all of its contents from the Arc E-Learning
              platform. This action is not reversible, so please continue with caution.
            </p>
            <div className="flex justify-end">
              <Button
                color="secondary-destructive"
                iconLeading={Trash01}
                onClick={() => setIsDeleteModalOpen(true)}
              >
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DestructiveHorizontalModal
        isOpen={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        title="Delete account"
        description="Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently lost."
        onConfirm={handleDeleteAccount}
        confirmButtonText="Delete"
        isLoading={deleteAccountMutation.isPending}
        icon={Trash01}
      />
    </div>
  );
}
